import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "../lib/api";
import Header from "../components/Header";
import {
  ArrowLeft, Play, Export, Trash, ArrowUpRight, RedditLogo, StackOverflowLogo,
  Plus, CheckCircle, XCircle, Eye, ChatCircle, Hash, Globe, Lock, DownloadSimple, Lightning,
} from "@phosphor-icons/react";

const TABS = ["leads", "sources", "runs", "settings"];

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } }
function timeAgo(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function elapsedString(run) {
  if (!run?.started_at) return "—";
  const start = new Date(run.started_at).getTime();
  const end = run.finished_at ? new Date(run.finished_at).getTime() : Date.now();
  const s = Math.max(0, Math.floor((end - start) / 1000));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem.toString().padStart(2, "0")}s` : `${s}s`;
}

function PlatformIcon({ platform, size = 14 }) {
  if (platform === "reddit") return <RedditLogo size={size} color="#FF4500" weight="fill" />;
  if (platform === "hackernews") return <span style={{color:"#FF6600",fontFamily:"JetBrains Mono",fontWeight:700,fontSize:size-2}}>Y</span>;
  if (platform === "stackexchange") return <StackOverflowLogo size={size} color="#F48024" weight="fill" />;
  if (platform === "web") return <Globe size={size} color="#00e5ff" weight="bold" />;
  if (platform === "manual") return <Lock size={size} color="#888" weight="bold" />;
  return <span className="text-[10px] text-[#888]">?</span>;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState("leads");
  const [err, setErr] = useState("");

  const loadProject = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [id]);

  useEffect(() => { loadProject(); }, [loadProject]);

  if (!project && !err) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="p-10 text-center text-[#888] font-mono text-xs">Loading project…</div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <div className="p-10 max-w-2xl mx-auto">
          <div className="p-3 border border-[#ff3b30]/40 bg-[#ff3b30]/10 text-[#ff3b30] text-xs font-mono">{err}</div>
        </div>
      </div>
    );
  }

  const startRun = async (deep = false) => {
    try {
      await api.post(`/projects/${id}/runs${deep ? "?deep=true" : ""}`);
      await loadProject();
      setTab("runs");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const exportCsv = async (format = "csv") => {
    try {
      const res = await api.get(`/projects/${id}/export?format=${format}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: format === "json" ? "application/json" : "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads-${project?.name?.replace(/\s+/g, "_") || id}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteProject = async () => {
    if (!confirm(`Delete project "${project.name}"? This removes leads, sources, and runs.`)) return;
    try { await api.delete(`/projects/${id}`); navigate("/"); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      <main className="px-6 md:px-10 py-8 max-w-[1500px] mx-auto" data-testid="project-detail-page">
        <button onClick={() => navigate("/")} className="btn-ghost mb-4 flex items-center gap-2" data-testid="back-to-dashboard">
          <ArrowLeft size={14} /> dashboard
        </button>

        {/* Header card */}
        <div className="panel mb-6">
          <div className="p-6 flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono text-[#555] uppercase tracking-[0.2em] mb-1">
                PROJ_{id.slice(0, 10)}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mb-2">{project.name}</h1>
              <p className="text-sm text-[#888] max-w-2xl">{project.problem_criteria || "No criteria defined."}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button data-testid="run-now-btn" onClick={() => startRun(false)} className="btn-primary flex items-center gap-2">
                <Play size={12} weight="fill" /> RUN NOW
              </button>
              <button
                data-testid="deep-scan-btn"
                onClick={() => {
                  if (confirm("Deep scan pulls up to 100 posts per source (vs. standard ~15). Takes longer and uses more LLM credits. Continue?")) {
                    startRun(true);
                  }
                }}
                className="btn-secondary flex items-center gap-2"
                title="Pulls up to 100 posts per source. Use for first-time backfill."
              >
                <Lightning size={12} weight="fill" color="#00e5ff" /> DEEP SCAN
              </button>
              <button data-testid="export-csv-btn" onClick={() => exportCsv("csv")} className="btn-secondary flex items-center gap-2">
                <Export size={12} /> CSV
              </button>
              <button data-testid="export-json-btn" onClick={() => exportCsv("json")} className="btn-secondary flex items-center gap-2">
                <Export size={12} /> JSON
              </button>
              <button data-testid="delete-project-btn" onClick={deleteProject} className="btn-secondary flex items-center gap-2 hover:!border-[#ff3b30] hover:!text-[#ff3b30]">
                <Trash size={12} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border-t border-[#2a2a2a]">
            {[
              { l: "TOTAL LEADS", v: project.stats?.leads_total ?? 0, c: "#ededed" },
              { l: "NEW", v: project.stats?.leads_new ?? 0, c: "#00ff66" },
              { l: "ACTIVE SOURCES", v: project.stats?.sources_active ?? 0, c: "#00e5ff" },
              { l: "STATUS", v: project.enabled ? "ACTIVE" : "PAUSED", c: project.enabled ? "#ffb000" : "#888" },
            ].map((s) => (
              <div key={s.l} className="bg-[#050505] p-4">
                <div className="label-tag mb-1">{s.l}</div>
                <div className="font-mono text-xl" style={{ color: s.c }}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#2a2a2a] mb-6 overflow-x-auto" data-testid="project-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] border-b-2 transition-colors ${
                tab === t ? "border-[#ffb000] text-[#ffb000]" : "border-transparent text-[#888] hover:text-[#ededed]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsTab projectId={id} />}
        {tab === "sources" && <SourcesTab projectId={id} />}
        {tab === "runs" && <RunsTab projectId={id} />}
        {tab === "settings" && <SettingsTab project={project} onChange={loadProject} />}
      </main>
    </div>
  );
}

// ----------------- Leads tab -----------------
function LeadsTab({ projectId }) {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("");
  const [platform, setPlatform] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (platform) params.set("platform", platform);
    try {
      const { data } = await api.get(`/projects/${projectId}/leads?${params}`);
      setLeads(data.leads || []);
    } finally { setLoading(false); }
  }, [projectId, status, platform]);

  useEffect(() => { load(); }, [load]);

  const setLeadStatus = async (leadId, newStatus) => {
    await api.patch(`/projects/${projectId}/leads/${leadId}`, { status: newStatus });
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));
  };

  return (
    <div data-testid="leads-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="label-tag mr-2">FILTER</span>
        {["", "new", "reviewed", "responded", "dismissed"].map((s) => (
          <button
            key={s || "all"}
            data-testid={`filter-status-${s || "all"}`}
            onClick={() => setStatus(s)}
            className={`btn-secondary ${status === s ? "!border-[#ffb000] !text-[#ffb000]" : ""}`}
          >
            {s || "all"}
          </button>
        ))}
        <span className="mx-2 text-[#2a2a2a]">|</span>
        {["", "reddit", "hackernews", "stackexchange"].map((p) => (
          <button
            key={p || "any"}
            data-testid={`filter-platform-${p || "any"}`}
            onClick={() => setPlatform(p)}
            className={`btn-secondary ${platform === p ? "!border-[#ffb000] !text-[#ffb000]" : ""}`}
          >
            {p || "any"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="panel p-8 text-center text-[#888] font-mono text-xs">Loading leads…</div>
      ) : leads.length === 0 ? (
        <div className="panel p-12 text-center" data-testid="empty-leads">
          <div className="text-[#888] mb-2 font-mono text-xs uppercase tracking-[0.2em]">/// no leads yet</div>
          <p className="text-[#555] text-sm">Hit RUN NOW or wait for the scheduler.</p>
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>SOURCE</th>
                <th>POST</th>
                <th>CONFIDENCE</th>
                <th>FOUND</th>
                <th>STATUS</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                  <td className="w-44">
                    <div className="flex items-center gap-2">
                      <PlatformIcon platform={l.platform} />
                      <span className="font-mono text-xs text-[#888] truncate">{l.source_ref}</span>
                    </div>
                  </td>
                  <td className="max-w-xl">
                    <div className="font-medium text-[#ededed] mb-1 line-clamp-2">{l.title || "(no title)"}</div>
                    <div className="text-xs text-[#888] line-clamp-2 mb-2">{l.snippet}</div>
                    <div className="text-[10px] text-[#555] font-mono italic line-clamp-2">
                      <ChatCircle size={10} className="inline mr-1" />
                      {l.match_reasoning || "—"}
                    </div>
                  </td>
                  <td className="w-28">
                    <ConfidenceBar value={l.match_confidence} />
                    <div className="font-mono text-[10px] text-[#888] mt-1 uppercase tracking-widest">{l.suggested_tone}</div>
                  </td>
                  <td className="w-28 font-mono text-xs text-[#888]">{timeAgo(l.found_at)}</td>
                  <td className="w-28">
                    <StatusPill status={l.status} />
                  </td>
                  <td className="w-44">
                    <div className="flex items-center gap-1 flex-wrap">
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-ghost flex items-center gap-1"
                        data-testid={`lead-open-${l.id}`}
                      >
                        OPEN <ArrowUpRight size={11} />
                      </a>
                      {l.status !== "reviewed" && (
                        <button onClick={() => setLeadStatus(l.id, "reviewed")} className="btn-ghost" data-testid={`lead-review-${l.id}`}>
                          <Eye size={12} />
                        </button>
                      )}
                      {l.status !== "responded" && (
                        <button onClick={() => setLeadStatus(l.id, "responded")} className="btn-ghost text-[#00ff66]" data-testid={`lead-responded-${l.id}`}>
                          <CheckCircle size={12} />
                        </button>
                      )}
                      {l.status !== "dismissed" && (
                        <button onClick={() => setLeadStatus(l.id, "dismissed")} className="btn-ghost text-[#ff3b30]" data-testid={`lead-dismiss-${l.id}`}>
                          <XCircle size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConfidenceBar({ value }) {
  const pct = Math.round((value || 0) * 100);
  const color = value >= 0.8 ? "#00ff66" : value >= 0.6 ? "#ffb000" : "#888";
  return (
    <div>
      <div className="font-mono text-xs" style={{ color }}>{pct}%</div>
      <div className="h-1 bg-[#1a1a1a] mt-1">
        <div className="h-1" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new: "tag-warning",
    reviewed: "tag-info",
    responded: "tag-success",
    dismissed: "tag-muted",
  };
  return <span className={`tag ${map[status] || "tag-muted"}`}>{status}</span>;
}

// ----------------- Sources tab -----------------
function SourcesTab({ projectId }) {
  const [sources, setSources] = useState([]);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const [newSrc, setNewSrc] = useState({ platform: "reddit", source_ref: "" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/sources`);
    setSources(data.sources || []);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (s) => {
    await api.patch(`/projects/${projectId}/sources/${s.id}?enabled=${!s.enabled}`);
    setSources((prev) => prev.map((x) => (x.id === s.id ? { ...x, enabled: !s.enabled } : x)));
  };
  const remove = async (s) => {
    if (!confirm(`Remove source ${s.source_ref}?`)) return;
    await api.delete(`/projects/${projectId}/sources/${s.id}`);
    setSources((prev) => prev.filter((x) => x.id !== s.id));
  };
  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/projects/${projectId}/sources`, { ...newSrc, enabled: true });
      setSources([data, ...sources]);
      setNewSrc({ platform: "reddit", source_ref: "" });
      setAdding(false);
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const importLibrary = async () => {
    if (!confirm("Import the curated 200+ source library into this project? Existing sources are kept.")) return;
    setImporting(true);
    setImportMsg("");
    try {
      const { data } = await api.post(`/projects/${projectId}/sources/import-library`);
      setImportMsg(`Added ${data.added} new sources (skipped ${data.skipped_existing} duplicates).`);
      await load();
    } catch (err) {
      setImportMsg(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setImporting(false);
    }
  };

  const grouped = sources.reduce((acc, s) => {
    (acc[s.platform] = acc[s.platform] || []).push(s);
    return acc;
  }, {});

  return (
    <div data-testid="sources-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="label-tag">{sources.length} TOTAL · {sources.filter((s) => s.enabled).length} ACTIVE</div>
        <div className="flex items-center gap-2">
          <button
            onClick={importLibrary}
            disabled={importing}
            className="btn-secondary flex items-center gap-2"
            data-testid="import-library-btn"
          >
            <DownloadSimple size={12} /> {importing ? "IMPORTING…" : "IMPORT 200+ LIBRARY"}
          </button>
          <button onClick={() => setAdding(!adding)} className="btn-primary flex items-center gap-2" data-testid="add-source-btn">
            <Plus size={12} weight="bold" /> ADD SOURCE
          </button>
        </div>
      </div>
      {importMsg && (
        <div className="mb-3 p-2 border border-[#2a2a2a] bg-[#0a0a0a] text-xs font-mono text-[#ededed]" data-testid="import-msg">
          {importMsg}
        </div>
      )}

      {adding && (
        <form onSubmit={add} className="panel p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="add-source-form">
          <select
            data-testid="new-source-platform"
            value={newSrc.platform}
            onChange={(e) => setNewSrc({ ...newSrc, platform: e.target.value })}
            className="field-input"
          >
            <option value="reddit">reddit</option>
            <option value="hackernews">hackernews</option>
            <option value="stackexchange">stackexchange</option>
            <option value="web">web (Discourse forum)</option>
            <option value="manual">manual (no auto-fetch)</option>
          </select>
          <input
            data-testid="new-source-ref"
            value={newSrc.source_ref}
            onChange={(e) => setNewSrc({ ...newSrc, source_ref: e.target.value })}
            placeholder={
              newSrc.platform === "reddit" ? "r/excel"
                : newSrc.platform === "hackernews" ? "hn"
                : newSrc.platform === "stackexchange" ? "stackoverflow"
                : newSrc.platform === "web" ? "community.openai.com"
                : "platform.com / channel name"
            }
            className="field-input"
            required
          />
          <button type="submit" className="btn-primary" data-testid="submit-new-source">CREATE</button>
        </form>
      )}

      {Object.entries(grouped).map(([platform, items]) => (
        <div key={platform} className="panel mb-4">
          <div className="panel-header">
            <div className="flex items-center gap-3">
              <PlatformIcon platform={platform} size={16} />
              <span className="font-semibold uppercase tracking-wider text-sm">{platform}</span>
              <span className="label-tag">[{items.length}]</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2a2a2a]">
            {items.map((s) => (
              <div key={s.id} className="bg-[#050505] p-3 flex items-center justify-between" data-testid={`source-row-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm text-[#ededed] truncate">{s.source_ref}</div>
                  {s.display_name && (
                    <div className="text-[10px] text-[#888] truncate">{s.display_name}{s.category ? ` · ${s.category}` : ""}</div>
                  )}
                  <div className={`text-[10px] font-mono mt-0.5 uppercase tracking-widest ${s.enabled ? "text-[#00ff66]" : "text-[#555]"}`}>
                    {s.enabled ? "monitoring" : "paused"}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => toggle(s)} className="btn-ghost" data-testid={`source-toggle-${s.id}`}>
                    {s.enabled ? <XCircle size={12} /> : <CheckCircle size={12} color="#00ff66" />}
                  </button>
                  <button onClick={() => remove(s)} className="btn-ghost hover:!text-[#ff3b30]" data-testid={`source-delete-${s.id}`}>
                    <Trash size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ----------------- Runs tab -----------------
function RunsTab({ projectId }) {
  const [runs, setRuns] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get(`/projects/${projectId}/runs`);
    setRuns(data.runs || []);
  }, [projectId]);
  useEffect(() => {
    load();
    // Pause polling when tab/page isn't visible to avoid background churn.
    let interval = null;
    const startPolling = () => {
      if (interval) return;
      interval = setInterval(load, 4000);
    };
    const stopPolling = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") startPolling();
      else stopPolling();
    };
    document.addEventListener("visibilitychange", onVisibility);
    if (document.visibilityState === "visible") startPolling();
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      stopPolling();
    };
  }, [load]);

  const active = runs.find((r) => r.status === "running");

  return (
    <div data-testid="runs-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tag">RUN HISTORY · auto-refreshing (3s)</div>
        {active && (
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[#ffb000]">
            <span className="pulse-dot w-1.5 h-1.5 bg-[#ffb000] inline-block" />
            SCANNING · {elapsedString(active)} elapsed
            {active.counts?.to_score && active.counts?.scored != null && (
              <span className="text-[#888]"> · {active.counts.scored}/{active.counts.to_score} scored</span>
            )}
          </div>
        )}
      </div>
      {runs.length === 0 ? (
        <div className="panel p-12 text-center text-[#888] font-mono text-xs">No runs yet.</div>
      ) : (
        <div className="panel overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>STARTED</th>
                <th>TRIGGER</th>
                <th>STATUS</th>
                <th>ELAPSED</th>
                <th>FETCHED</th>
                <th>SCORING</th>
                <th>MATCHED</th>
                <th>SAVED</th>
                <th>NOTES</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const elapsed = elapsedString(r);
                const scored = r.counts?.scored;
                const toScore = r.counts?.to_score;
                const isRunning = r.status === "running";
                return (
                <tr key={r.id} data-testid={`run-row-${r.id}`}>
                  <td className="font-mono text-xs text-[#888]">{fmt(r.started_at)}</td>
                  <td><span className="tag tag-muted">{r.trigger}</span></td>
                  <td>
                    <span className={`tag ${r.status === "success" ? "tag-success" : isRunning ? "tag-warning" : "tag-error"}`}>
                      {isRunning && <span className="pulse-dot inline-block w-1.5 h-1.5 bg-[#ffb000] mr-1.5" />}
                      {r.status}
                    </span>
                  </td>
                  <td className="font-mono text-xs text-[#ededed]">{elapsed}</td>
                  <td className="font-mono text-sm">{r.counts?.fetched ?? "—"}</td>
                  <td className="font-mono text-sm">
                    {toScore != null && scored != null ? (
                      <>
                        <span className={isRunning ? "text-[#ffb000]" : "text-[#ededed]"}>{scored}</span>
                        <span className="text-[#555]">/{toScore}</span>
                      </>
                    ) : (scored ?? "—")}
                  </td>
                  <td className="font-mono text-sm text-[#ffb000]">{r.counts?.matched ?? "—"}</td>
                  <td className="font-mono text-sm text-[#00ff66]">{r.counts?.saved ?? "—"}</td>
                  <td className="text-xs text-[#888] max-w-xs truncate">{r.error || ""}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ----------------- Settings tab -----------------
function SettingsTab({ project, onChange }) {
  const [form, setForm] = useState({
    name: project.name || "",
    product_description: project.product_description || "",
    problem_criteria: project.problem_criteria || "",
    suggested_response: project.suggested_response || "",
    keywords: (project.keywords || []).join("\n"),
    schedule_hours: project.schedule_hours,
    confidence_threshold: project.confidence_threshold,
    per_source_limit: project.per_source_limit,
    enabled: project.enabled,
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      await api.patch(`/projects/${project.id}`, {
        ...form,
        keywords: form.keywords.split(/[\n,]/).map((k) => k.trim()).filter(Boolean),
        schedule_hours: Number(form.schedule_hours),
        confidence_threshold: Number(form.confidence_threshold),
        per_source_limit: Number(form.per_source_limit),
      });
      setMsg("Saved.");
      onChange?.();
    } catch (e) {
      setMsg(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <form onSubmit={save} className="panel p-6 space-y-5" data-testid="settings-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="field-label">Name</label>
          <input className="field-input" value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Schedule (hours)</label>
          <input type="number" min={1} className="field-input" value={form.schedule_hours} onChange={(e) => set("schedule_hours", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Confidence threshold</label>
          <input type="number" min={0} max={1} step={0.05} className="field-input" value={form.confidence_threshold} onChange={(e) => set("confidence_threshold", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Per-source limit</label>
          <input type="number" min={1} max={50} className="field-input" value={form.per_source_limit} onChange={(e) => set("per_source_limit", e.target.value)} />
        </div>
      </div>
      <div>
        <label className="field-label">Product description</label>
        <textarea rows={3} className="field-input resize-none" value={form.product_description} onChange={(e) => set("product_description", e.target.value)} />
      </div>
      <div>
        <label className="field-label">Problem criteria</label>
        <textarea rows={3} className="field-input resize-none" value={form.problem_criteria} onChange={(e) => set("problem_criteria", e.target.value)} />
      </div>
      <div>
        <label className="field-label">Suggested response template</label>
        <textarea rows={3} className="field-input resize-none" value={form.suggested_response} onChange={(e) => set("suggested_response", e.target.value)} />
      </div>
      <div>
        <label className="field-label">Keywords (newline or comma)</label>
        <textarea rows={4} className="field-input resize-none" value={form.keywords} onChange={(e) => set("keywords", e.target.value)} />
      </div>
      <label className="flex items-center gap-3 cursor-pointer select-none">
        <input data-testid="settings-enabled-toggle" type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
        <span className="text-sm">Scheduler enabled (auto-runs on schedule)</span>
      </label>

      <div className="flex items-center gap-3 pt-2">
        <button type="submit" disabled={busy} className="btn-primary" data-testid="save-settings-btn">
          {busy ? "SAVING…" : "SAVE CHANGES"}
        </button>
        {msg && <span className="text-xs font-mono text-[#888]">{msg}</span>}
      </div>
    </form>
  );
}
